// src/myclipsv3/feed/discoveryUrl.ts
//
// IG paritesi — keşif overlay URL'leri: /sounds/{soundId} ve /clips/search[?q=].
//
// ÖNEMLİ: Bu modül feed OYNATMA/pager/poster mantığına DOKUNMAZ. Sadece keşif
// overlay'i (discoveryView: search|sound) açılıp kapanırken adres çubuğunu
// replaceState ile günceller (overlay = aynı katman → history bloat YOK). Overlay
// açılırken önceki feed URL'si (/c/{clipId} vb.) saklanır, kapanışta geri yüklenir.
// Cold deep-link: parseDiscoveryFromUrl ile mount'ta overlay durumu kurulur.

import { susturRouterPopIcin } from "../../utils/sheetRouteGuard";

let _prevUrl: string | null = null;
// PR-V3e2b2 — bu overlay uygulama-içi pushState ile mi açıldı? (cold-load'da false).
// restoreDiscoveryUrl bununla history.back() vs replaceState arasında karar verir.
let _pushedDiscoveryEntry = false;

// Overlay açılışı: önceki feed URL'sini sakla + discovery URL'sine PUSH.
// PR-V3e2b2 — replaceState → pushState: history entry üretir → tarayıcı GERİ
// tuşu overlay'i kapatır (popstate → dispatcher → mlv3:discovery-sync{null}).
export function pushDiscoveryUrl(kind: string, arg?: string): void {
  try {
    const cur = window.location.pathname + window.location.search;
    let target: string | null = null;
    if (kind === "sound" && arg) target = `/sounds/${encodeURIComponent(arg)}`;
    else if (kind === "search") target = `/clips/search`;
    // PR-V3e3 — hashtag: /clips/tags/{tag}. Boş/eksik tag → target null → push YOK.
    else if (kind === "hashtag" && arg) target = `/clips/tags/${encodeURIComponent(arg)}`;
    // PR-V3e5 — trending leaderboard (section): /clips/trending/{section}.
    else if (kind === "trending" && arg) target = `/clips/trending/${encodeURIComponent(arg)}`;
    // PR-V3e5 — challenge: /clips/challenge/{id}. Boş id → push YOK.
    else if (kind === "challenge" && arg) target = `/clips/challenge/${encodeURIComponent(arg)}`;
    if (!target || cur === target) return;
    if (_prevUrl == null) _prevUrl = cur; // yalnız feed→overlay ilk geçişte sakla
    window.history.pushState(window.history.state, "", target);
    _pushedDiscoveryEntry = true;
  } catch {
    /* URL kozmetik — hata feed'i etkilemez */
  }
}

// Arama sorgusu değişince /clips/search?q={q} yansıt (yalnız arama açıkken).
export function setSearchQueryUrl(q: string): void {
  try {
    if (window.location.pathname !== "/clips/search") return;
    const target = q ? `/clips/search?q=${encodeURIComponent(q)}` : `/clips/search`;
    if (window.location.pathname + window.location.search === target) return;
    window.history.replaceState(window.history.state, "", target);
  } catch {
    /* yoksay */
  }
}

// Overlay kapanışı (X/backToFeed): PR-V3e2b2 —
//  - Uygulama-içi pushState ile açıldıysa → history.back() (pushed entry'yi pop et;
//    popstate dispatcher overlay'i senkronlar → X ve donanım-GERİ TEK yoldan çalışır,
//    stack şişmez). backToFeed'in setDiscoveryView(null)'ı overlay'i anında kapatır;
//    back'in geç popstate'i null'u re-teyit eder (no-op).
//  - Cold-load'da (push yok) → eski replaceState fallback (saklı feed URL'si veya
//    "/"; uygulamadan çıkmaz). history.back() cold-load'da siteden çıkarabilirdi.
export function restoreDiscoveryUrl(): void {
  try {
    const pushed = _pushedDiscoveryEntry;
    const back = _prevUrl;
    _prevUrl = null;
    _pushedDiscoveryEntry = false;
    if (pushed) {
      // ── #1021 — ROUTER SUSTURMA PENCERESİ ────────────────────────────
      // ÖLÇÜLDÜ (gerçek Chromium, ürünün GERÇEK modülünü koşturan sonda):
      //     öncesi="/c/clipB" → açılış="/clips/search" → kapanış="/c/clipB"
      //     pop sayısı=1 · router SIRASINDAKİ dinleyici sustu = **false**
      // Yani bu `back()`in pop'unda global router UYANIK ve altta duran
      // `/c/{id}`yi soğuk derin bağlantı sanıp legacy klip modalını
      // açıyor — kapanan overlay'in yerine bir modal.
      // `capture` + `stopImmediatePropagation()` burada bir SEÇENEK DEĞİL:
      // keşif kapanışının kendi popstate dinleyicisi yok, olsaydı bile
      // AT_TARGET sırası yüzünden router'a ULAŞAMAZDI (bkz. guard başlığı).
      // Sıradan bağımsız tek kaldıraç sayaçtır.
      susturRouterPopIcin();
      window.history.back();
      return;
    }
    if (back != null) {
      window.history.replaceState(window.history.state, "", back);
      return;
    }
    // Cold-open (prevUrl yok) ama hâlâ discovery URL'sindeyiz → feed köküne dön;
    // clip-sync ardından /c/{görünen} yazar.
    if (/^\/sounds\/|^\/clips\/search/.test(window.location.pathname)) {
      window.history.replaceState(window.history.state, "", "/");
    }
  } catch {
    /* yoksay */
  }
}

// Overlay'den belirli bir klibe atlanırken (grid tap): saklanan URL'yi geri YÜKLEME
// — clip-sync /c/{tıklanan} yazacak; sadece saklı durumu temizle.
export function clearDiscoveryUrl(): void {
  _prevUrl = null;
  _pushedDiscoveryEntry = false;
}

// PR-V3e4a — Feed tab cold-load: /clips/{following|neighborhood|saved} → tab.
// SAF (pathname param'lı; default window). Tab bir OVERLAY DEĞİL (activeTab) →
// discovery-sync köprüsünden AYRI; yalnız cold-load mount-init'te okunur. Tab
// yoksa (/clips = discover, /clips/search, /c/... vb.) → null (çağıran initialTab
// fallback kullanır). Tab MODU replaceState kuralına tabi; bu PR yalnız cold-load
// okur, YAZMAZ (tab click URL yazımı V3e4b).
export function tabFromUrl(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
  search: string = typeof window !== "undefined" ? window.location.search || "" : "",
): "following" | "neighborhood" | "saved" | null {
  try {
    const s = parseDiscoveryRoute(pathname, "");
    if (s && s.kind === "tab") return s.tab;
    // Klip oynatıcısındayken sekme bağlamı SORGUDA taşınır (bkz.
    // `klipUrliSekmeyleYaz` başlığındaki yol-vs-sorgu gerekçesi).
    return sekmeyiKlipSorgusundanOku(pathname, search);
  } catch {
    return null;
  }
}

// ─── PR-V3e4b — Feed tab LIVE URL (replaceState) + popstate tab sync ──────────
// KURAL: modlar replace, katmanlar push. Tab bir MOD → replaceState (history
// şişmez, GERİ feed'den çıkar). Overlay'ler (search/sound/hashtag/trending/
// challenge) pushState olarak KALIR — dokunulmaz.

export const TAB_SYNC_EVENT = "mlv3:tab-sync";

export type FeedTabName = "following" | "neighborhood" | "saved" | "discover";

// activeTab → canonical tab URL'sini REPLACESTATE ile yaz. following/neighborhood/
// saved → /clips/{tab}; discover/diğer → /clips. Aynı path ise no-op. pushState
// ASLA. NOT: overlay açıkken çağıran taraf (FeedContainerV3 effect) bunu ATLAR
// (overlay URL'yi ezmesin) — burada yalnız aynı-path koruması var.
export function writeTabUrl(tab: string): void {
  try {
    const target =
      tab === "following" || tab === "neighborhood" || tab === "saved"
        ? `/clips/${tab}`
        : "/clips"; // discover / default
    if (window.location.pathname === target) return;
    window.history.replaceState(window.history.state, "", target);
  } catch {
    /* URL kozmetik — hata feed'i etkilemez */
  }
}

// popstate → tab sync detail (SAF). /clips/{tab} → o tab; bare /clips → discover;
// overlay (search/sound/tags/trending/challenge) veya non-clips → tab:null
// (= "tab'ı DEĞİŞTİRME"; overlay altındaki tab korunur). Böylece overlay URL'sine
// popstate tab'ı sıfırlamaz.
export function buildTabSyncDetail(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
  search: string = typeof window !== "undefined" ? window.location.search || "" : "",
): { tab: FeedTabName | null } {
  try {
    const s = parseDiscoveryRoute(pathname, "");
    if (s && s.kind === "tab") return { tab: s.tab };
    if (s && s.kind === "clips-home") return { tab: "discover" };
    // `/c/{id}?tab=...` → geri/ileri o sekmeye döner. SEKMESİZ klip URL'si
    // (paylaşılan temiz link) yine tab:null → activeTab'e DOKUNULMAZ; geri
    // tuşu sekmeyi SIFIRLAMAZ (mevcut davranış korunur).
    const q = sekmeyiKlipSorgusundanOku(pathname, search);
    if (q) return { tab: q };
    return { tab: null };
  } catch {
    return { tab: null };
  }
}

// ─── PR-V3e4c — Feed tab edge-case guard'ları (SAF, test-edilebilir) ──────────
// V3e4b'de bu iki mantık FeedContainerV3 effect'lerinde inline'dı; regresyona en
// açık noktalar bunlar (overlay-skip + popstate no-op). Saf fonksiyonlara
// çıkarıldı ki edge davranışı unit-test ile KİLİTLENSİN. Davranış birebir aynı.

// Tab writer guard: mount (first-run) VEYA overlay açıkken tab URL'si YAZILMAZ.
//  - first-run → cold-load URL'sini (/c/{id}, /clips/search, /clips/{tab}) ezme.
//  - overlay açık → katman (overlay) URL'sini ezme; alttaki mod korunur.
// true → yazımı ATLA; false → writeTabUrl çağrılabilir.
export function shouldSkipTabUrlWrite(opts: {
  firstRun: boolean;
  overlayOpen: boolean;
}): boolean {
  return !!(opts.firstRun || opts.overlayOpen);
}

// Popstate tab-sync hedefi: detail.tab null (overlay/non-clips URL) → null
// (activeTab'e DOKUNMA); mevcut tab ile aynı → null (gereksiz setState + writer
// no-op yok); farklı geçerli tab → uygulanacak tab. Loop-prevention: aynı-tab'da
// null döner, ayrıca writeTabUrl'ın same-path guard'ı ile birlikte replaceState
// döngüsü oluşmaz.
export function resolveTabSyncTarget(
  detail: { tab: FeedTabName | null } | null | undefined,
  currentTab: string,
): FeedTabName | null {
  const t = detail?.tab;
  if (t == null) return null;
  if (t === currentTab) return null;
  return t;
}

// Cold deep-link: mount'ta URL'den overlay durumunu çöz.
// PR-V3e1 — artık saf `parseDiscoveryRoute`'a delege eder; DIŞ DAVRANIŞ AYNI:
// yalnız halihazırda BAĞLI kind'lar (sound + search) dış çıktıya yansır. Yeni
// şema kind'ları (clips-home/tab/hashtag/trending/challenge) V3e2+'da runtime'a
// bağlanana kadar mount overlay'ini AKTİFLEMEZ (bugünkü null davranışı korunur).
export function parseDiscoveryFromUrl():
  | { kind: string; soundId?: string; q?: string; tag?: string; section?: string; challengeId?: string }
  | null {
  try {
    const s = parseDiscoveryRoute(
      window.location.pathname,
      window.location.search || "",
    );
    if (s && s.kind === "sound") return { kind: "sound", soundId: s.soundId };
    if (s && s.kind === "search") return { kind: "search", q: s.q || "" };
    // PR-V3e3 — hashtag cold-load: /clips/tags/{tag} → mount'ta hashtag overlay.
    if (s && s.kind === "hashtag") return { kind: "hashtag", tag: s.tag };
    // PR-V3e5 — trending (section) + challenge cold-load. Bare /clips/trending
    // (section yok) → FeedContainerV3'te overlay yok → mount init null (feed).
    if (s && s.kind === "trending" && s.section) return { kind: "trending", section: s.section };
    if (s && s.kind === "challenge") return { kind: "challenge", challengeId: s.challengeId };
    return null;
  } catch {
    return null;
  }
}

// ─── PR-V3e1 — Discovery URL saf model + parser/serializer harness ─────────────
//
// SAF (DOM/window/side-effect YOK) → discoveryUrl.test.ts ile robotla kilitlenir.
// Sonraki V3e2 (/clips base + back/forward), V3e3 (hashtag), V3e4 (feed tab),
// V3e5 (trending/challenge) PR'ları bu harness'la kanıtlanır. Bu PR yalnız
// helper+test ekler; runtime push/replace/overlay davranışını DEĞİŞTİRMEZ.
//
// Kabul edilen HİBRİT şema (owner onaylı): /clips base + nested discovery;
// mevcut GLOBAL /sounds/{id} korunur (kırma yok). /tags, /challenge, /trending
// GLOBAL DEĞİL → /clips/* altında → yeni RESERVED gerekmez.

export type DiscoveryTab = "following" | "neighborhood" | "saved";
export type TrendingSection = "sounds" | "hashtags" | "creators";

export type DiscoveryRouteState =
  | { kind: "clips-home" }
  | { kind: "tab"; tab: DiscoveryTab }
  | { kind: "search"; q?: string }
  | { kind: "hashtag"; tag: string }
  | { kind: "sound"; soundId: string }
  | { kind: "trending"; section?: TrendingSection }
  | { kind: "challenge"; challengeId: string };

const DISCOVERY_TABS: DiscoveryTab[] = ["following", "neighborhood", "saved"];

// ─── Klip permalink'i + sekme bağlamı (yol-vs-sorgu) ─────────────────────────
//
// 🩺 ÖLÇÜLEN KUSUR: `/clips/{tab}` yolu V3e4'te kuruldu ve DOĞRU çalışıyordu,
// ama adres çubuğunda TEK yazıcı değildi. `MyClipsV3Gate.onActiveClipChange`
// aktif klip değişince KOŞULSUZ klip yolunu yazıyordu → kullanıcı "Yakın"a
// geçtikten saniyeler sonra sekme adres çubuğundan siliniyordu. Yani hata
// "sekme hiç yazılmıyor" DEĞİL, **"yazılan sekmeyi ikinci yazıcı eziyor"**du.
// İki yazıcı aynı adres çubuğu için yarışıyordu ve klip kazanıyordu.
//
// 🧭 KARAR — YOL vs SORGU. Deponun ölçülmüş kuralı: *yol = GÖRÜNÜM, sorgu = o
// görünümün İÇİNDEKİ daraltma* (`gorunumFiltreUrl.ts` emsali).
//   · çıplak akış görünümü               → YOL  `/clips/{tab}`  (DEĞİŞMEDİ)
//   · klip oynatıcı görünümü             → YOL  `/c/{id}`       (DEĞİŞMEDİ;
//     paylaşım hedefi, IG `/reels/{id}` muadili)
//   · oynatıcının HANGİ akışı sayfaladığı → SORGU `?tab=`
// Sekme başka bir GÖRÜNÜM değil; aynı oynatıcının hangi listeyi sayfaladığıdır
// → daraltma → sorgu. İç içe bir YOL (`/clips/{tab}/c/{id}`) da mümkündü ama
// (a) yeni matcher + yeni rezerve yüzeyi ister, (b) klip paylaşım hedefini
// ikiye böler, (c) sekme kişiye özeldir — senin "takip"in benim "takip"im
// değildir; paylaşılan YOLA gömülmesi yanlış olurdu.
//
// `discover` VARSAYILANDIR → sorgu YAZILMAZ. Böylece yaygın durumda paylaşılan
// bağlantı birebir eskisi gibi temiz kalır.
export const KLIP_SEKME_PARAM = "tab";

const KLIP_YOLU_RE = /^\/c\/[^/]+/;

// `?tab=` YALNIZ klip yolunda okunur. Profil/harita gibi başka yollara
// yapıştırılan aynı sorgu akış sekmesini ele geçiremez.
function sekmeyiKlipSorgusundanOku(
  pathname: string,
  search: string,
): DiscoveryTab | null {
  try {
    if (!KLIP_YOLU_RE.test(String(pathname || ""))) return null;
    const ham = new URLSearchParams(String(search || "")).get(KLIP_SEKME_PARAM);
    if (!ham) return null;
    return DISCOVERY_TABS.includes(ham as DiscoveryTab)
      ? (ham as DiscoveryTab)
      : null; // uydurma değer YOK SAYILIR
  } catch {
    return null;
  }
}

/**
 * Aktif klip değişince adres çubuğuna yazılacak hedef (SAF; DOM okumaz/yazmaz).
 *
 * Mevcut adresten sekme bağlamını devralır: `/clips/saved` üzerindeyse ya da
 * zaten `?tab=saved` taşıyorsa yeni klip URL'si de o sekmeyi taşır. Böylece
 * sekme klipten klibe sayfalarken KAYBOLMAZ ve yenileme/paylaşım aynı akışa
 * döner.
 *
 * @returns yazılacak hedef, ya da **null** = "yazma" (geçersiz id VEYA adres
 *          zaten hedefte — gereksiz `replaceState` üretilmez).
 */
export function klipUrliSekmeyleYaz(
  clipId: string | null | undefined,
  mevcutPath: string,
  mevcutSearch = "",
): string | null {
  const id = clipId == null ? "" : String(clipId).trim();
  if (!id) return null;
  const taban = `/c/${encodeURIComponent(id)}`;
  const yol = String(mevcutPath || "");
  const sorgu = String(mevcutSearch || "");
  const s = parseDiscoveryRoute(yol, "");
  const sekme: DiscoveryTab | null =
    s && s.kind === "tab" ? s.tab : sekmeyiKlipSorgusundanOku(yol, sorgu);
  const hedef = sekme ? `${taban}?${KLIP_SEKME_PARAM}=${sekme}` : taban;
  return `${yol}${sorgu}` === hedef ? null : hedef;
}
const TRENDING_SECTIONS: TrendingSection[] = ["sounds", "hashtags", "creators"];

function decodeSeg(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

// URL → discovery route state (SAF). Trailing-slash toleranslı. Tanınmayan/
// geçersiz (boş tag/sound/challenge, bilinmeyen trending section) → null.
// NOT: /sounds/{id} GLOBAL korunur; /clips/sounds/{id}, /tags/{tag},
// /challenge/{id} canonical DEĞİL (bilinçli → null).
export function parseDiscoveryRoute(
  pathname: string,
  search = "",
): DiscoveryRouteState | null {
  try {
    const raw = String(pathname || "");
    const path = raw.length > 1 ? raw.replace(/\/+$/, "") : raw;

    // Global (korunan) — /sounds/{id}
    const mSound = path.match(/^\/sounds\/([^/]+)$/);
    if (mSound) {
      const soundId = decodeSeg(mSound[1]).trim();
      return soundId ? { kind: "sound", soundId } : null;
    }

    // /clips ailesi
    if (path === "/clips") return { kind: "clips-home" };

    const mTab = path.match(/^\/clips\/(following|neighborhood|saved)$/);
    if (mTab) return { kind: "tab", tab: mTab[1] as DiscoveryTab };

    if (path === "/clips/search") {
      const sp = new URLSearchParams(search || "");
      const q = sp.get("q");
      return q ? { kind: "search", q } : { kind: "search" };
    }

    const mTag = path.match(/^\/clips\/tags\/([^/]+)$/);
    if (mTag) {
      const tag = decodeSeg(mTag[1]).trim();
      return tag ? { kind: "hashtag", tag } : null;
    }

    if (path === "/clips/trending") return { kind: "trending" };
    const mTrend = path.match(/^\/clips\/trending\/([^/]+)$/);
    if (mTrend) {
      const section = decodeSeg(mTrend[1]) as TrendingSection;
      return TRENDING_SECTIONS.includes(section)
        ? { kind: "trending", section }
        : null;
    }

    const mChallenge = path.match(/^\/clips\/challenge\/([^/]+)$/);
    if (mChallenge) {
      const challengeId = decodeSeg(mChallenge[1]).trim();
      return challengeId ? { kind: "challenge", challengeId } : null;
    }

    return null;
  } catch {
    return null;
  }
}

// discovery route state → canonical URL (SAF). Geçersiz state (boş tag/sound/
// challenge, bilinmeyen tab/section) → null. Query encode edilir.
export function serializeDiscoveryRoute(
  state: DiscoveryRouteState | null | undefined,
): string | null {
  if (!state || typeof state !== "object") return null;
  switch (state.kind) {
    case "clips-home":
      return "/clips";
    case "tab":
      return DISCOVERY_TABS.includes(state.tab) ? `/clips/${state.tab}` : null;
    case "search": {
      const q = typeof state.q === "string" ? state.q.trim() : "";
      return q ? `/clips/search?q=${encodeURIComponent(q)}` : "/clips/search";
    }
    case "hashtag": {
      const tag = typeof state.tag === "string" ? state.tag.trim() : "";
      return tag ? `/clips/tags/${encodeURIComponent(tag)}` : null;
    }
    case "sound": {
      const id = typeof state.soundId === "string" ? state.soundId.trim() : "";
      return id ? `/sounds/${encodeURIComponent(id)}` : null;
    }
    case "trending":
      if (state.section == null) return "/clips/trending";
      return TRENDING_SECTIONS.includes(state.section)
        ? `/clips/trending/${state.section}`
        : null;
    case "challenge": {
      const id =
        typeof state.challengeId === "string" ? state.challengeId.trim() : "";
      return id ? `/clips/challenge/${encodeURIComponent(id)}` : null;
    }
    default:
      return null;
  }
}

// ─── PR-V3e2b1 — Discovery sync KÖPRÜSÜ (dormant; davranış DEĞİŞTİRMEZ) ─────────
//
// Back/forward parity ALTYAPISI. Bu PR yalnız köprüyü kurar; RUNTIME'da hiçbir
// yer bu event'i dispatch ETMEZ (popstate→dispatch + replaceState→pushState V3e2b2).
// Dolayısıyla FeedContainerV3'teki listener PRATİKTE UYUYOR → davranış değişmez.
//
// SAF fonksiyonlar (DOM/window/side-effect YOK) → discoveryUrl.test.ts ile
// robotla kanıtlanır. PII/GPS/userId TAŞIMAZ; URL yazmaz; push/replaceState çağırmaz.

export const DISCOVERY_SYNC_EVENT = "mlv3:discovery-sync";

export type DiscoverySyncSource = "popstate" | "route-sync" | "test";

export type DiscoverySyncDetail = {
  state: DiscoveryRouteState | null;
  source: DiscoverySyncSource;
};

// URL → discovery-sync detail (V3e2b2 dispatcher'ı bunu kullanacak). Discovery
// dışı / geçersiz path → state:null (overlay KAPAT niyeti). Saf.
export function buildDiscoverySyncDetail(
  pathname: string,
  search = "",
  source: DiscoverySyncSource = "route-sync",
): DiscoverySyncDetail {
  return { state: parseDiscoveryRoute(pathname, search), source };
}

// FeedContainerV3'ün discoveryView shape'i (V3 legacy): trending kind'ları
// `trending-{section}` düz string; diğerleri DiscoveryRouteState ile hizalı.
type DiscoveryViewValue =
  | null
  | { kind: "search"; q: string }
  | { kind: "sound"; soundId: string }
  | { kind: "hashtag"; tag: string }
  | { kind: "challenge"; challengeId: string }
  | { kind: "trending-sounds" | "trending-hashtags" | "trending-creators" };

// discovery-sync detail → FeedContainerV3 discoveryView değeri (SAF).
// apply:false → LISTENER NO-OP (malformed/tanınmayan → discoveryView'e DOKUNMA).
// apply:true + view:null → overlay KAPAT. apply:true + view:{...} → overlay AÇ.
// clips-home / tab → overlay değil (tab activeTab ile, V3e4) → apply:true, view:null
// yalnız clips-home (feed base = overlay kapalı); tab → no-op (activeTab'e dokunma).
export function resolveDiscoveryViewFromSync(
  detail: unknown,
): { apply: boolean; view: DiscoveryViewValue } {
  const NOOP = { apply: false, view: null as DiscoveryViewValue };
  if (!detail || typeof detail !== "object" || !("state" in (detail as any))) {
    return NOOP;
  }
  const s = (detail as any).state;
  if (s === null) return { apply: true, view: null }; // açık KAPAT niyeti
  if (!s || typeof s !== "object" || typeof s.kind !== "string") return NOOP;
  switch (s.kind) {
    case "clips-home":
      return { apply: true, view: null };
    case "search":
      return { apply: true, view: { kind: "search", q: typeof s.q === "string" ? s.q : "" } };
    case "sound":
      return s.soundId ? { apply: true, view: { kind: "sound", soundId: s.soundId } } : NOOP;
    case "hashtag":
      return s.tag ? { apply: true, view: { kind: "hashtag", tag: s.tag } } : NOOP;
    case "challenge":
      return s.challengeId
        ? { apply: true, view: { kind: "challenge", challengeId: s.challengeId } }
        : NOOP;
    case "trending":
      if (s.section === "sounds") return { apply: true, view: { kind: "trending-sounds" } };
      if (s.section === "hashtags") return { apply: true, view: { kind: "trending-hashtags" } };
      if (s.section === "creators") return { apply: true, view: { kind: "trending-creators" } };
      return NOOP;
    case "tab":
      // Feed tab overlay DEĞİL (activeTab, V3e4) → discoveryView'e dokunma.
      return NOOP;
    default:
      return NOOP;
  }
}
